import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { PageWrapper, PageHeaderWithMarginA } from '../utilities/commonUtilities/Layout';
import { Header1BMargin } from '../utilities/commonUtilities/Typography';
import { SmallBody1 } from '../utilities/commonUtilities/GenericSpanElements';
import { makeStyles, Grid, TableContainer, Table, TableBody, TableCell, TableRow, Button, Box, Typography } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { actionSaga } from '../../sagas/dispatchSaga';
import { put, call } from 'redux-saga/effects';
import { actionPutObservationIntoStore, actionAppendObservationIntoStore, loadObservations, setObservationsLoadingResult } from '../../actions';
import { getObservations, getObservationsPagable } from '../../apis/observationFhir';
import { DetailsLayout } from '../utilities/layouts/DetailsLayout';
import { ContentLayout } from '../utilities/layouts/ContentLayout';
import { styleBreakMobile } from '../utilities/layouts/Globals';
import { FONT_WEIGHT_SEMI } from '../utilities/Material';
import { PATH_LABS } from '../../containers/RoutePaths';
import { RequiresLogin } from '../utilities/commonUtilities/RequiresLogin';
import { LoadingResult, LoadingStatus } from '../../utils/LoadingUtils';
import { EmptyItem } from '../utilities/commonUtilities/CardItem';
import { ReactComponent as LabEmpty } from '../../resources/images/lab-empty.svg';
import { COLOR_WHITE } from '../utilities/MaterialWatchman';
import clsx from 'clsx';

const isWatchman = process.env.REACT_APP_THEME_ENV === 'watchman';

/** observation style components **/
const useStyles = makeStyles(theme => ({
    dataSection: {
        background: theme.palette.background.default,
        minHeight: '80vh', // HACKASAURUS: I cannot get the height of the data section to fill the rest of the vertical space.
    },
    largeObservationValueTypography: {
        fontSize: '70px',
        lineHeight: '96px',
        color: isWatchman ? theme.palette.secondary.contrastText : theme.palette.info.contrastText,
        [styleBreakMobile(theme)]: {
            fontSize: '56px',
            lineHeight: '70px',
        },
    },
    latestResult: {
        marginBottom: theme.spacing(2),
    },
    latestResultTypography: {
        fontWeight: FONT_WEIGHT_SEMI,
    },
    currentObservationSection: {
        marginBottom: theme.spacing(4),
        [styleBreakMobile(theme)]: {
            marginBottom: theme.spacing(3),
        },
    },
    pastObservationTypography: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
    },
    gridItem: {
        marginLeft: '5px',
    },
    tableCell: {
        fontSize: '16px',
        fontFamily: 'Avenir Next',
        fontStyle: 'normal',
        fontWeight: 'normal',
        paddingLeft: '0px',
        paddingRight: '0px',
    },
    button: {
        textTransform: 'none',
        width: '100%',
        marginTop: '15px',
        marginBottom: '15px',
    },
    largeText: {
        fontSize: theme.spacing(5),
        lineHeight: '55px',
        fontWeight: 700,
        fontStyle: 'normal',
        [styleBreakMobile(theme)]: {
            fontSize: theme.spacing(4),
            textAlign: 'center',
            lineHeight: '41px',
        },
    },
    subtitleText: {
        letterSpacing: '0.3px',
    },
    subtitleLabText: {
        color: theme.palette.secondary.contrastText,
    },
    rangesTable: {
        backgroundColor: theme.palette.background.default,
        paddingRight: theme.spacing(3),
        paddingLeft: theme.spacing(3),
        borderRadius: theme.spacing(1),

        '& tr:last-child > td': {
            borderBottom: 'none',
        },
    },
    rangesTitle: {
        fontSize: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    modalDescription: {
        marginBottom: theme.spacing(2),
        display: 'block',
    },
    labName: {
        padding: `${theme.spacing(1.5)}px 0`,
    },
    rangeValue: {
        padding: `${theme.spacing(1.5)}px 0`,
    },
}));

const ObservationPageWrapper = styled(PageWrapper)`
    justify-content: flex-start;
    height: auto;
`;

const FixedObservationCard = styled.div`
    position: fixed;
    background: #ffffff;
    width: 100%;
    left: 0;
`;

const FixedEmptyObservationCard = styled(FixedObservationCard)`
    height: 40%;
`;

const NoObservationResult = styled.div`
    font-weight: 500;
    font-size: 24px;
    line-height: 33px;
    margin-top: 15px;
`;

export const observationCodes = {
    PSA: {
        code: ['2857-1'],
        name: 'PSA',
        key: 'PSA',
    },
    Testosterone: {
        code: ['14913-8', '2986-8'],
        name: 'Testosterone',
        key: 'Testosterone',
    },
    HCG: {
        code: [
            '19080-1',
            '21198-7',
            '2111-3',
            '2110-5',
            '2119-6',
            '2112-1',
            '45194-8',
            '2106-3',
            '53959-3',
            '2118-8',
            '53958-5',
            '2107-1',
            '44001-6',
            '42891-2',
        ],
        name: 'Human Chorionic Gonadotropin (HCG)',
        key: 'HCG',
    },
    AFP: {
        code: ['1834-1', '53962-7', '53960-1', '1833-3', '11207-8'],
        name: 'Alpha-fetoprotein (AFP)',
        key: 'AFP',
    },
    LDH: {
        code: [
            '14804-9',
            '14805-6',
            '2532-0',
            '2529-6',
            '14803-1',
            '60017-1',
            '2530-4',
            '2531-2',
            '2528-8',
            '60024-7',
            '60023-9',
            '60019-7',
            '33367-4',
            '2533-8',
            '60020-5',
            '75639-5',
        ],
        name: 'Lactate Dehydrogenase (LDH)',
        key: 'LDH',
    },
    FSH: {
        code: ['2286-3', '15067-2', '20433-9', '14291-9'],
        name: 'Follicle-stimulating Hormone (FSH)',
        key: 'FSH',
    },
    LH: {
        code: ['2579-1', '10501-5', '20419-8'],
        name: 'Luteinizing Hormone (LH)',
        key: 'LH',
    },
};

/** Observation components and page **/
const DISPLAY_ITEM_LENGTH = 25;

function ResultPageHeader({ history, observationCode }) {
    return (
        <>
            <PageHeaderWithMarginA onPrevClick={() => history.push(PATH_LABS)} />
            <Header1BMargin>{observationCode.name}</Header1BMargin>
        </>
    );
}

function ObservationTable({ styling, observationResults }) {
    return (
        <TableContainer>
            <Table>
                <TableBody>
                    {observationResults.map((row, indx) => (
                        <TableRow key={indx}>
                            <TableCell className={styling.tableCell} align="left">
                                <Typography variant="body1">{row.date}</Typography>
                            </TableCell>
                            <TableCell className={styling.tableCell} align="right">
                                <Typography variant="body1">
                                    {row.comparator ? row.comparator + ' ' : ''}
                                    {row.value} {row.unit ? row.unit : ''}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

const LabCentres = {
    UHN: 'UHN',
    LIFE: 'LifeLabs',
    DYNA: 'DynaCare',
};

const rangesData = {
    [observationCodes.HCG.key]: {
        title: 'Human Chorionic Gonadotropin (HCG)',
        description:
            'This test measures the level of a protein called Human Chorionic Gonadotropin (HCG) in your blood. Many, but not all testicular cancers make high levels of tumour marker proteins like HCG. Elevated levels of HCG may indicate your testicular tumour has relapsed.',
        abbr: observationCodes.HCG.key,
        unit: 'IU/L',
        ranges: [
            { lab: LabCentres.UHN, value: '<3' },
            { lab: LabCentres.LIFE, value: '<3' },
            { lab: LabCentres.DYNA, value: '<3' },
        ],
    },
    [observationCodes.LDH.key]: {
        title: 'Lactate Dehydrogenase (LDH)',
        description:
            'This test measures the level of a protein called Lactate Dehydrogenase (LDH) in your blood. LDH is less specific for testis cancer than HCG or AFP, but can be indicative of tissue damage or disease in your body.',
        abbr: observationCodes.LDH.key,
        unit: 'U/L',
        ranges: [
            { lab: LabCentres.UHN, value: '125-220' },
            { lab: LabCentres.LIFE, value: '110-230' },
            { lab: LabCentres.DYNA, value: '110-215' },
        ],
    },
    [observationCodes.FSH.key]: {
        title: 'Follicle-stimulating Hormone (FSH)',
        description: `This test measures the level of a protein called Follicle-stimulating Hormone (FSH) in your blood. FSH is the brain's signal to the testicle to make sperm.  We monitor this to help us understand how your remaining testicle is functioning. Abnormalities in FSH do NOT indicate a relapse of cancer.`,
        abbr: observationCodes.FSH.key,
        unit: 'IU/L',
        ranges: [
            { lab: LabCentres.UHN, value: '1-12' },
            { lab: LabCentres.LIFE, value: '1-8' },
            { lab: LabCentres.DYNA, value: '1-16' },
        ],
    },
    [observationCodes.LH.key]: {
        title: 'Luteinizing Hormone (LH)',
        description: `This test measures the level of a protein called Luteinizing Hormone (LH) in your blood. LH is the brain's signal to the testicle to make testosterone. We monitor this to help us understand how your remaining testicle is functioning. Abnormalities in LH do NOT indicate a relapse of cancer.`,
        abbr: observationCodes.LH.key,
        unit: 'IU/L',
        ranges: [
            { lab: LabCentres.UHN, value: '0.6-12.1' },
            { lab: LabCentres.LIFE, value: '1-7' },
            { lab: LabCentres.DYNA, value: '1.3-9.6' },
        ],
    },
    [observationCodes.AFP.key]: {
        title: 'Alpha-fetoprotein (AFP)',
        description: `This test measures the level of a protein called Alpha-fetoprotein (AFP) in your blood. Many, but not all testicular cancers make high levels of tumour marker proteins like AFP. Elevated levels of AFP may indicate that your testicular tumour has relapsed.`,
        abbr: observationCodes.AFP.key,
        unit: 'ug/L',
        ranges: [
            { lab: LabCentres.UHN, value: '<= 8.9' },
            { lab: LabCentres.LIFE, value: '<8.4' },
            { lab: LabCentres.DYNA, value: '<8' },
        ],
    },
    [observationCodes.Testosterone.key]: {
        title: 'Testosterone',
        description: `This test measures the amount of total Testosterone in your blood. Testosterone is a male sex hormone (androgen). We monitor this to help us understand how your remaining testicle is functioning. Abnormalities in Testosterone do NOT indicate a relapse of cancer.`,
        abbr: observationCodes.Testosterone.key,
        unit: 'nmol/L',
        ranges: [
            { lab: LabCentres.UHN, value: '7.7-30.2' },
            { lab: LabCentres.LIFE, value: '8.4-28.8' },
            { lab: LabCentres.DYNA, value: '7.6-31.4' },
        ],
    },
    [observationCodes.PSA.key]: {
        title: 'Prostate-specific antigen (PSA)',
        description: 'PSA test information goes here.',
        abbr: observationCodes.PSA.key,
        unit: 'ug/L',
        ranges: [],
    },
};

function InfoModalContent({ details }) {
    const classes = useStyles();
    return (
        <Box>
            <Typography variant="caption" className={classes.modalDescription}>
                {details.description}
            </Typography>

            {details.ranges?.length > 0 && (
                <>
                    <Typography variant="h3" className={classes.rangesTitle}>
                        Normal Ranges by Labs
                    </Typography>
                    <TableContainer className={classes.rangesTable}>
                        <Table>
                            <TableBody>
                                {details?.ranges.map((row, indx) => (
                                    <TableRow key={indx}>
                                        <TableCell className={classes.labName} align="left">
                                            <Typography variant="body1">{row.lab}</Typography>
                                        </TableCell>
                                        <TableCell className={classes.rangeValue} align="right">
                                            <Typography variant="body1">
                                                {row.value} {details.unit}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
}

function NonEmptyObservationResult({ styling, observationResults, moreObservationResults, handleShowMore, endIndx, observationCode }) {
    const latestObservation = observationResults[0];
    const latestDate = latestObservation.date;
    const latestObsValue = latestObservation.value;
    const latestObsComparator = latestObservation.comparator;
    const latestObsUnit = latestObservation.unit;
    //Remove latest observation from array to not display in table
    const resultsCopy = observationResults.slice(1);

    const infoButtonPopoverTexts = {
        PSA: <InfoModalContent details={rangesData[observationCodes.PSA.key]} />,
        Testosterone: <InfoModalContent details={rangesData[observationCodes.Testosterone.key]} />,
        'Alpha-fetoprotein (AFP)': <InfoModalContent details={rangesData[observationCodes.AFP.key]} />,
        'Human Chorionic Gonadotropin (HCG)': <InfoModalContent details={rangesData[observationCodes.HCG.key]} />,
        'Lactate Dehydrogenase (LDH)': <InfoModalContent details={rangesData[observationCodes.LDH.key]} />,
        'Follicle-stimulating Hormone (FSH)': <InfoModalContent details={rangesData[observationCodes.FSH.key]} />,
        'Luteinizing Hormone (LH)': <InfoModalContent details={rangesData[observationCodes.LH.key]} />,
    };

    const observationResult = (
        // We cannot use content layout here as the maxwidth of the container must be able to span the screen.
        <Grid container direction="column">
            <ContentLayout className={styling.currentObservationSection}>
                <Grid container wrap="nowrap" alignItems="baseline">
                    <Grid item>
                        <Typography variant="h1" className={styling.largeObservationValueTypography}>
                            {latestObsComparator ? latestObsComparator + ' ' : ''}
                            {latestObsValue}
                        </Typography>
                    </Grid>
                    <Grid item className={styling.gridItem}>
                        <SmallBody1>{latestObsUnit ? latestObsUnit : ''}</SmallBody1>
                    </Grid>
                </Grid>
                <Box className={styling.latestResult}>
                    <Typography variant="caption" className={styling.latestResultTypography}>
                        Latest result on {latestDate}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="caption">
                        {`${observationCode.name} results can be interpreted in different ways. Please contact your healthcare provider to learn what these results mean for you.`}
                    </Typography>
                </Box>
            </ContentLayout>
            <Box flexGrow={1} className={styling.dataSection}>
                <ContentLayout>
                    <Typography variant="h4" className={styling.pastObservationTypography}>
                        PAST RESULTS
                    </Typography>
                    {resultsCopy.length > 0 && (
                        <ObservationTable styling={styling} observationResults={resultsCopy.slice(0, Math.min(resultsCopy.length, endIndx))} />
                    )}
                    {// https://collaborate.uhnresearch.ca/jira/browse/NEDVC-451 will be removing this code.
                    moreObservationResults && (
                        <Button className={styling.button} onClick={handleShowMore}>
                            <Typography style={{ color: COLOR_WHITE }}>Show More</Typography>
                        </Button>
                    )}
                </ContentLayout>
            </Box>
        </Grid>
    );

    return <DetailsLayout backTo={PATH_LABS} title={observationCode.name} infoText={infoButtonPopoverTexts} children={observationResult} />;
}

function EmptyObservationResult({ history, observationCode }) {
    const classes = useStyles();
    const SubtitleText = () => {
        return (
            <>
                <Typography className={clsx(classes.largeText, classes.subtitleText)}>
                    Sorry,
                    <br />
                    No{' '}
                    <Typography className={clsx(classes.largeText, classes.subtitleLabText)} component="span">
                        {observationCode.name}
                    </Typography>{' '}
                    Results Available.
                </Typography>
            </>
        );
    };

    return isWatchman ? (
        <div style={{ height: '100vh', background: COLOR_WHITE }}>
            <EmptyItem
                image={LabEmpty}
                title={SubtitleText}
                message={`${observationCode.name} results completed for your virtual follow-up will show up here.`}
                isFullscreen={isWatchman}
                showShadow={isWatchman}
            />
        </div>
    ) : (
        <FixedEmptyObservationCard>
            <ObservationPageWrapper>
                <ResultPageHeader history={history} observationCode={observationCode} />
                <NoObservationResult>{`You currently do not have any ${observationCode.name} data.`}</NoObservationResult>
            </ObservationPageWrapper>
        </FixedEmptyObservationCard>
    );
}

function ObservationPageContent({ fetchObservation, patientFhirId, observationResults, observationCode }) {
    const styling = useStyles();
    const history = useHistory();
    const [endIndx, setEndIndx] = useState(DISPLAY_ITEM_LENGTH);
    const [moreObservationResults, setMoreObservationResults] = useState(observationResults.length > 0 ? true : false);

    // Handle for browser reload on labs page
    const onReloadEvent = e => {
        e.preventDefault();
        fetchObservation(patientFhirId);
    };

    useEffect(() => {
        window.addEventListener('beforeunload', onReloadEvent);
        window.addEventListener('load', onReloadEvent);

        return () => {
            window.removeEventListener('beforeunload', onReloadEvent);
            window.removeEventListener('load', onReloadEvent);
        };
    });

    useEffect(() => {
        // (-1) is to exclude latest observation from table
        if (observationResults.length - 1 <= DISPLAY_ITEM_LENGTH) {
            setMoreObservationResults(false);
        } else {
            setMoreObservationResults(true);
        }
    }, [observationResults]);

    const handleShowMore = () => {
        if (endIndx + DISPLAY_ITEM_LENGTH >= observationResults.length - 1) {
            // reached end of observationResult list
            setMoreObservationResults(false);
        }
        setEndIndx(endIndx + DISPLAY_ITEM_LENGTH);
    };

    return (
        <>
            {observationResults.length > 0 ? (
                <NonEmptyObservationResult
                    history={history}
                    styling={styling}
                    observationResults={observationResults}
                    moreObservationResults={moreObservationResults}
                    handleShowMore={handleShowMore}
                    endIndx={endIndx}
                    observationCode={observationCode}
                />
            ) : (
                <EmptyObservationResult history={history} observationCode={observationCode} />
            )}
        </>
    );
}

// This wraps the RequiresLogin around the actual content so the useEffect (lab fetch) will not be necessary without a login.
function ObservationPage({ fetchObservation, patientFhirId, observationResults, observationCode }) {
    return (
        <RequiresLogin>
            <ObservationPageContent
                fetchObservation={fetchObservation}
                patientFhirId={patientFhirId}
                observationResults={observationResults}
                observationCode={observationCode}
            />
        </RequiresLogin>
    );
}

/** Hooking up observation component to redux store **/
function mapStateToProps(state) {
    return {
        patientFhirId: state.login.session.patientId,
    };
}

const mapDispatchToProps = {
    fetchObservation: actionObservation,
};

export default connect(mapStateToProps, mapDispatchToProps)(ObservationPage);

/** Data fetching layer of Observation functionality  **/
export function actionObservation(patientFhirId) {
    return actionSaga('OBSERVATION_FETCH_FROM_SERVER', observationSaga, patientFhirId);
}

function* observationSaga(patientFhirId) {
    yield put(loadObservations(LoadingStatus.STARTED));
    try {
        const response = yield call(getObservations, patientFhirId);
        if (response.status === 200) {
            const nextPageObj = yield call(storeObservation, response, actionPutObservationIntoStore);
            if (nextPageObj) {
                yield call(observationPagingSaga, nextPageObj);
            }
            yield put(setObservationsLoadingResult(LoadingResult.SUCCESSFUL));
        } else {
            console.log(response);
            yield put(setObservationsLoadingResult(LoadingResult.FAILED));
        }
    } catch (e) {
        console.log(e);
        yield put(setObservationsLoadingResult(LoadingResult.FAILED));
    }
    yield put(loadObservations(LoadingStatus.ENDED));
}

function* observationPagingSaga(nextPageObj) {
    while (nextPageObj) {
        let response = yield call(getObservationsPagable, nextPageObj.url);
        if (response.status === 200) {
            nextPageObj = yield call(storeObservation, response, actionAppendObservationIntoStore);
        } else {
            break;
        }
    }
}

/**
 * Stores current page of observations into store.
 * Returns next page if it exists.
 */
function* storeObservation(response, actionCreator) {
    const observations = response.data.entry || [];
    yield put(actionCreator(observations));
    return response.data.link.find(item => item.relation === 'next');
}
